/**
 * Cookie Consent Banner
 *
 * Minimal bottom banner with Accept/Decline buttons.
 * Dark glassmorphism style matching the Haive theme.
 *
 * Only shown when consent status is 'undecided'.
 * Analytics cookies are only loaded after the user accepts.
 */

import { Box, Button, Group, Text } from '@mantine/core';
import { IconCookie } from '@tabler/icons-react';
import { AnimatePresence, motion } from 'framer-motion';
import { useConsentStore } from '../../analytics/consent';

export function CookieConsent() {
  const status = useConsentStore((s) => s.status);
  const accept = useConsentStore((s) => s.accept);
  const decline = useConsentStore((s) => s.decline);

  return (
    <AnimatePresence>
      {status === 'undecided' && (
        <motion.div
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
          transition={{ duration: 0.35, ease: 'easeOut' }}
          style={{
            position: 'fixed',
            bottom: 24,
            left: 0,
            right: 0,
            margin: '0 auto',
            zIndex: 9999,
            width: '92%',
            maxWidth: 520,
          }}
        >
          <Box
            p="md"
            style={{
              background: 'rgba(20, 20, 22, 0.92)',
              backdropFilter: 'blur(24px)',
              WebkitBackdropFilter: 'blur(24px)',
              border: '1px solid rgba(255, 255, 255, 0.08)',
              borderRadius: 16,
              boxShadow: '0 8px 40px rgba(0, 0, 0, 0.5)',
            }}
          >
            <Group gap="md" wrap="nowrap" align="flex-start">
              <IconCookie
                size={22}
                color="#fd7e14"
                style={{ flexShrink: 0, marginTop: 2 }}
              />
              <Box style={{ flex: 1, minWidth: 0 }}>
                <Text size="sm" c="white" fw={600} mb={4}>
                  We use cookies
                </Text>
                <Text size="xs" c="dimmed" lh={1.5}>
                  We use analytics cookies to understand how you use Haive and
                  improve the experience. No personal data is tracked.
                </Text>
              </Box>
            </Group>

            <Group justify="flex-end" gap="xs" mt="sm">
              <Button
                variant="subtle"
                color="gray"
                size="compact-sm"
                onClick={decline}
              >
                Decline
              </Button>
              <Button
                color="orange"
                size="compact-sm"
                onClick={accept}
              >
                Accept
              </Button>
            </Group>
          </Box>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
